<template>
  <div class="pass-history-view v3-pass-history v3-pass-history--reports">
    <Loader
      :is-processing="!!state?.isLoading"
      :loader-text="$t('common.a11y_loading_pass_totals')"
    />
    <ActionPanel class="mb-3" show-top-left-slot>
      <template #left-content>
        <div class="mt-3">
          <div class="d-flex flex-column mb-3">
            <div
              class="d-flex mb-3 align-items-start flex-wrap gap-2 pass-totals-filters"
              :class="{
                'pass-totals-filters--year-hidden': !hasSelectableGradeYears
              }"
            >
              <CustomSelect
                v-if="hasSelectableGradeYears"
                v-model="state.filterParams.grade_years"
                class="pt-selector flex-shrink-0 ms-1 me-2"
                :options="gradeYears"
                placeholder="Select year"
                :close-on-select="true"
                accessible-label="School year"
                input-id="pass-totals-grade-year"
                @update:model-value="setCalendarGradeYears"
              ></CustomSelect>
              <CustomSchoolYearSelectDatePicker
                class="pt-selector flex-grow-1 min-w-0 ms-1"
                v-model="state.filterParams.dates"
                :range-options="optionalDateRanges"
                :is-current-school-year-selected="currentSchoolYearSelected"
                :selected-school-year-limit="state?.filterParams?.grade_years"
                range-accessible-label="Date range"
                range-input-id="pass-totals-date-range"
              ></CustomSchoolYearSelectDatePicker>
            </div>
            <div v-if="!isStartDateValid" class="invalid-message text-start">
              Start date cannot be a future date
            </div>
          </div>
        </div>
      </template>
      <template #bottom-content>
        <div class="justify-content-end text-end">
          <CAlert
            v-if="state?.errors && !state?.isLoading"
            dismissible
            color="danger"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            @close="dismissPassTotalsError"
          >
            {{ state?.errors }}
          </CAlert>
          <InfoBox
            v-if="showReloadButtonAndProgressMessage"
            :key="'pass-totals-progress-' + progressMessage"
            :message="progressMessage"
            class="mt-2"
          />
          <BaseButton
            v-if="showReloadButtonAndProgressMessage"
            class="ms-2"
            rounded
            @click="handleReload()"
          >
            Reload
          </BaseButton>
          <BaseButton
            :disabled="!isStartDateValid"
            class="ms-2"
            solid
            rounded
            @click="submit()"
            >Submit</BaseButton
          >
          <BaseButton class="ms-2" rounded @click="clearFilter()"
            >Clear all</BaseButton
          >
        </div>
      </template>
    </ActionPanel>
    <PerPage
      v-if="state?.pagination && state?.pagination?.total > 0"
      :pagination="state?.pagination"
      :showing-entries="true"
      @on-page-change="setPerPage"
    />

    <DataTable
      v-if="!state?.isLoading"
      class="datatable bg-white text-center mt-3"
      add-table-classes="totalPassReportsDatatable"
      :fields="dataTableFields"
      :items="passReports"
      :no-items-view="{
        noResults: 'No items available',
        noItems: 'No items available'
      }"
    >
      <template #start_date="{ item }">
        <td>{{ item?.csv_data?.start_date }}</td>
      </template>
      <template #end_date="{ item }">
        <td>{{ item?.csv_data?.end_date }}</td>
      </template>
      <template #total_passes="{ item }">
        <td>
          {{ item?.csv_data?.total_passes || 0 }}
        </td>
      </template>
      <template #total_stu_passes="{ item }">
        <td>
          {{ item?.csv_data?.stu_passes || 0 }}
        </td>
      </template>
      <template #total_tch_passes="{ item }">
        <td>
          {{ item?.csv_data?.tch_passes || 0 }}
        </td>
      </template>
      <template #total_apt_passes="{ item }">
        <td>
          {{ item?.csv_data?.apt_passes || 0 }}
        </td>
      </template>
      <template #total_ksk_passes="{ item }">
        <td>
          {{ item?.csv_data?.ksk_passes || 0 }}
        </td>
      </template>
      <template #total_slp_passes="{ item }">
        <td>
          {{ activeSchool.is_premium ? item?.csv_data?.slp_passes || 0 : 0 }}
        </td>
      </template>
      <template #requested_date="{ item }">
        <td>
          {{
            item?.request_date
              ? $helpers.transformUTC(item.request_date, "MM/DD/YYYY h:mm A")
              : "-"
          }}
        </td>
      </template>
      <template #last_rerun_date="{ item }">
        <td>
          {{
            item?.last_rerun_date
              ? $helpers.transformUTC(item.last_rerun_date, "MM/DD/YYYY h:mm A")
              : "-"
          }}
        </td>
      </template>
      <template #action="{ item }">
        <td class="pass-totals-action-td">
          <div v-if="isReportReRunning(item)" class="spinner-container">
            <CSpinner color="primary" />
          </div>
          <div v-else class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <span class="me-2">{{ item?.status || "In Progress" }}</span>
              <i
                v-show="isReportIdInList(item)"
                title="Number of Passes have increased"
                class="ri-information-line"
                aria-hidden="true"
              />
            </div>
            <div class="d-flex align-items-center pass-totals-action-controls">
              <BaseButton
                class="ms-2 d-flex justify-content-center align-items-center"
                solid
                rounded
                @click="reRun(item)"
                :is-disabled="
                  !isReportIdInList(item) || isReportInProgress(item)
                "
                style="width: 120px"
                >{{ isReportInProgress(item) ? "In Progress" : "Re-run" }}
              </BaseButton>
              <IconButton
                class="static"
                :icon="`ri-refresh-line`"
                aria-label="Refresh report status"
                :tabindex="refreshIconTabindex(item)"
                :style="{
                  visibility:
                    isReportInProgress(item) || showRefresh(item)
                      ? 'visible'
                      : 'hidden'
                }"
                @click="refresh(item)"
              />
              <IconButton
                class="static"
                :icon="`ri-download-2-line`"
                :aria-label="downloadCsvAriaLabel(item)"
                :class="{
                  'blurred-icon': isReportInProgress(item) || showRefresh(item)
                }"
                :disabled="item?.status !== 'done'"
                @click="item?.status === 'done' ? downloadCsv(item) : undefined"
              />
            </div>
          </div>
        </td>
      </template>
    </DataTable>

    <Pagination
      v-if="state?.pagination?.pages > 1 && !state?.isLazyLoadingMode"
      :active-page="state?.pagination?.activePage || 1"
      :pages="state?.pagination?.pages || 0"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    ></Pagination>
  </div>
</template>

<script>
import { reactive, computed, onMounted, onUnmounted, watch } from "vue"
import { useI18n } from "vue-i18n"
import { isAllEntriesPerPageOption } from "@/constants/tablePaginationConstants.js"
import { attachMainLayoutLazyScroll } from "@/helpers/tableLazyScroll.js"
import { useStore } from "vuex"
import moment from "moment-timezone"
import { useGradeYears } from "@/composables/useGradeYears.js"
import { announce } from "@/composables/useLiveAnnouncer"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import ActionPanel from "../shared/DataTable/ActionPanel.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import helpersJS from "@/helpers/index"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import CustomSchoolYearSelectDatePicker from "@/v3components/shared/Form/CustomSchoolYearSelectDatePicker.vue"

export default {
  name: "PassTotalsTable",
  components: {
    Loader,
    ActionPanel,
    CustomSelect,
    BaseButton,
    DataTable,
    IconButton,
    PerPage,
    InfoBox,
    CustomSchoolYearSelectDatePicker
  },
  setup() {
    const store = useStore()
    const { t } = useI18n()
    const {
      gradeYears,
      currentSchoolYear,
      hasSelectableGradeYears,
      getMinDate,
      getMaxDate,
      watchSelectedGradeYear
    } = useGradeYears()
    const state = reactive({
      isLazyLoadingMode: false,
      isLoading: false,
      isReportReRun: {},
      reportStatus: {
        latestReportStatus: null,
        latestReportFilters: null
      },
      reportIds: [],
      reRunStatus: {
        reRunButtonText: {},
        reportStatus: {},
        lastReRunDate: {}
      },
      filterParamsDateRange: null,
      filterParams: {
        grade_years: [],
        dates:
          moment().format("MM/DD/YYYY") + " - " + moment().format("MM/DD/YYYY")
      },
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      errors: ""
    })

    const dismissPassTotalsError = () => {
      state.errors = ""
    }

    watch(
      () => state.errors,
      (message) => {
        const text =
          typeof message === "string"
            ? message.trim()
            : message != null
              ? String(message).trim()
              : ""
        if (!text) return
        announce(text, { assertive: true })
      }
    )

    const maxDate = computed(() => {
      return getMaxDate(state?.filterParams?.grade_years)
    })

    const minDate = computed(() => {
      return getMinDate(state?.filterParams?.grade_years)
    })

    const optionalDateRanges = computed(() => {
      if (!state?.filterParams?.grade_years?.value || !minDate.value) {
        return [
          {
            label: "Today",
            id: "",
            range: helpersJS.stringDateRange(moment(), moment())
          }
        ]
      }

      const getRange = (days) => {
        const today = moment()
        const start = today.clone().subtract(days, "days")

        const minSchoolYearDate = moment({
          year: minDate.value.getFullYear(),
          month: minDate.value.getMonth(),
          date: minDate.value.getDate()
        })

        const adjustedStart = start.isBefore(minSchoolYearDate)
          ? minSchoolYearDate
          : start

        return helpersJS.stringDateRange(adjustedStart, today)
      }

      return [
        {
          label: "Today",
          id: "",
          range: helpersJS.stringDateRange(moment(), moment())
        },
        {
          label: "Yesterday",
          id: "",
          range: helpersJS.stringDateRange(
            moment().subtract(1, "days"),
            moment().subtract(1, "days")
          )
        },
        {
          label: "Last 7 days",
          id: "",
          range: getRange(7)
        },
        {
          label: "Last 14 days",
          id: "",
          range: getRange(14)
        },
        {
          label: "Last 30 days",
          id: "",
          range: getRange(30)
        },
        {
          label: "This month",
          id: "",
          range: helpersJS.stringDateRange(
            moment().startOf("month"),
            moment().endOf("month")
          )
        },
        {
          label: "This year",
          id: "",
          range:
            helpersJS.currTzDate(minDate.value) +
            " - " +
            helpersJS.currTzDate(maxDate.value)
        },
        {
          label: "Custom Range",
          id: "custom",
          range: null
        }
      ]
    })

    const isStartDateValid = computed(() => {
      const startDateFormatted = moment(
        state?.filterParams?.dates?.split(" - ")?.[0],
        "MM/DD/YYYY"
      )
      const currentDateFormatted = moment()
      return startDateFormatted.isSameOrBefore(currentDateFormatted)
    })

    const setCalendarGradeYears = () => {
      if (state?.filterParams) {
        state.filterParams.dates =
          helpersJS.currTzDate(minDate.value) +
          " - " +
          helpersJS.currTzDate(maxDate.value)
        selectRange(
          helpersJS.currTzDate(minDate.value) +
            " - " +
            helpersJS.currTzDate(maxDate.value)
        )
      }
    }

    watchSelectedGradeYear(
      () => state.filterParams?.grade_years,
      (nextGradeYear) => {
        if (state?.filterParams) {
          state.filterParams.grade_years = nextGradeYear
        }
      },
      () => {
        setCalendarGradeYears()
      }
    )

    const selectRange = (range) => {
      if (state) {
        state.filterParamsDateRange = {
          start: range.substring(0, range.indexOf(" -")),
          end: range.substring(range.indexOf("- ") + 2)
        }
      }
    }

    const currentSchoolYearSelected = computed(() => {
      return (
        state?.filterParams?.grade_years?.label ==
        currentSchoolYear.value?.label
      )
    })

    const clearFilter = () => {
      clearFilterParams()
    }

    const clearFilterParams = () => {
      if (state) {
        state.errors = ""
      }
      if (state?.filterParams) {
        state.filterParams = Object.assign(state.filterParams, {
          grade_years: gradeYears.value
            ? gradeYears.value[gradeYears.value.length - 1]
            : state.filterParams?.grade_years,
          dates:
            moment().format("MM/DD/YYYY") +
            " - " +
            moment().format("MM/DD/YYYY")
        })
      }
    }
    const setPerPage = (option) => {
      if (option && state) {
        state.isLazyLoadingMode = isAllEntriesPerPageOption(option)
        if (state.pagination) {
          state.pagination.per_page = option
        }
        handleReports()
      }
    }

    //DATATABLE
    const dataTableFields = [
      {
        key: "start_date",
        label: "Start Date",
        type: "tabular",
        _style: "min-width:125px"
      },
      {
        key: "end_date",
        label: "End Date",
        type: "tabular",
        _style: "min-width:125px"
      },
      {
        key: "total_passes",
        label: "Total Passes",
        type: "tabular"
      },
      {
        key: "total_stu_passes",
        label: "STU Passes",
        type: "tabular"
      },
      {
        key: "total_tch_passes",
        label: "TCH Passes",
        type: "tabular"
      },
      {
        key: "total_apt_passes",
        label: "APT Passes",
        type: "tabular"
      },
      {
        key: "total_ksk_passes",
        label: "KSK Passes",
        type: "tabular"
      },
      {
        key: "total_slp_passes",
        label: "SLP Passes",
        type: "tabular"
      },
      {
        key: "requested_date",
        label: "Requested Date",
        type: "tabular"
      },
      {
        key: "last_rerun_date",
        label: "Last Re-run Date",
        type: "tabular"
      },
      {
        key: "action",
        label: "Action",
        type: "tabular"
      }
    ]

    const activeSchool = computed(() => store.getters["schools/activeSchool"])

    const passReportRequestData = computed(() => {
      const dates = state?.filterParams?.dates
      const [start, end] =
        dates && typeof dates === "string" ? dates.split(" - ") : [null, null]
      return {
        grad_year: state?.filterParams?.grade_years?.value?.[0],
        date_range: {
          start: start,
          end: end
        }
      }
    })

    const showRefresh = (report) => {
      return (
        getReportStatus(report) === "inprogress" ||
        Boolean(getLastReRunDate(report))
      )
    }

    const isReportIdInList = (item) => {
      if (!item?.id) return false
      return listOfReportIds.value.includes(item.id)
    }

    const getReRunButtonText = (item) => {
      if (!item?.id) return ""
      return state?.reRunStatus?.reRunButtonText?.[item.id] || ""
    }

    const isReportInProgress = (item) => {
      return getReRunButtonText(item) === "In Progress"
    }

    /** Keep hidden refresh icon out of the tab order (visibility:hidden does not always remove it). */
    const refreshIconTabindex = (item) => {
      return isReportInProgress(item) || showRefresh(item) ? 0 : -1
    }

    const getReportStatus = (report) => {
      if (!report?.id) return null
      return state.reRunStatus?.reportStatus?.[report.id] || null
    }

    const getLastReRunDate = (report) => {
      if (!report?.id) return null
      return state?.reRunStatus?.lastReRunDate?.[report.id] || null
    }

    const isReportReRunning = (item) => {
      if (!item?.id) return false
      return Boolean(state?.isReportReRun?.[item.id])
    }

    const showReloadButtonAndProgressMessage = computed(() => {
      const status = state?.reportStatus?.latestReportStatus || null
      return status === "pending" || status === "inprogress"
    })

    const progressMessage = computed(() => {
      const filters = state?.reportStatus?.latestReportFilters || null
      if (!filters?.grad_year || !filters?.date_range) {
        return ""
      }

      const gradeYearLabel = state?.filterParams?.grade_years?.label || ""
      const startDate = filters?.date_range?.start || ""
      const endDate = filters?.date_range?.end || ""

      return `Report with filters: Academic Year: ${gradeYearLabel}, Start Date: ${startDate}, End Date: ${endDate} is In Progress. Please wait for "New" badge to appear on left hand menu, then click "Reload" to see its status.`
    })

    watch(
      () =>
        showReloadButtonAndProgressMessage.value ? progressMessage.value : "",
      (message) => {
        if (!message?.trim?.()) return
        announce(message)
      }
    )

    const submit = () => {
      if (state) {
        state.errors = ""
      }
      store
        .dispatch(
          "totalPassReports/submitTotalPassReportRequest",
          passReportRequestData.value
        )
        .then((response) => {
          if (response?.data?.error && state) {
            state.errors = response?.data?.error
          } else {
            if (state) {
              state.errors = ""
            }
            showLatestReportStatus()
          }
        })
        .catch((err) => {
          if (state) {
            state.errors = err
          }
        })
    }

    const setActivePage = (page) => {
      if (state?.pagination) {
        state.pagination.activePage = page
        handleReports(state.pagination.activePage)
      }
      helpersJS.scrollToTop()
    }

    let detachMainLayoutLazyScroll = null
    const initLazyLoading = () => {
      detachMainLayoutLazyScroll?.()
      detachMainLayoutLazyScroll = attachMainLayoutLazyScroll(() => {
        if (
          state?.isLazyLoadingMode &&
          passReports.value &&
          Array.isArray(passReports.value) &&
          passReports.value.length &&
          state?.pagination?.total > passReports?.value?.length
        ) {
          if (state?.pagination) {
            state.pagination.activePage = (state.pagination.activePage || 0) + 1
            handleReports(state.pagination.activePage, true, false)
          }
        }
      })
    }

    const handleReports = (page, isLazyLoadingMode, noLoader = true) => {
      if (!state) return
      state.isLoading = !noLoader
      if (state.pagination) {
        state.pagination.activePage = page ? page : 1
      }
      getPassReports(page, isLazyLoadingMode)
    }

    const downloadCsv = (item) => {
      // Assume you have a reportId property in the item object
      const reportId = item?.id
      // Make an API call to download the file
      store
        .dispatch("totalPassReports/exportTotalPassReportCsv", reportId)
        .then((response) => {
          const fileContents = response?.data?.url?.original?.fileContents
          const fileName = response?.data?.url?.original?.fileName
          const blob = new Blob([fileContents], {
            type: "text/csv;charset=utf-8;"
          })

          const link = document.createElement("a")
          const url = URL.createObjectURL(blob)
          link.href = url
          link.setAttribute("download", fileName)

          // Append link to the body and trigger the download
          document.body.appendChild(link)
          link.click()

          // Cleanup
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        })
        .catch((err) => {
          // Fixing eslint error
          // reject(err)
          // eslint-disable-next-line no-console
          console.error("Download failed:", err)
        })
    }

    const downloadCsvAriaLabel = (item) => {
      const start = item?.csv_data?.start_date || ""
      const end = item?.csv_data?.end_date || ""
      const id = item?.id != null ? String(item.id) : ""
      if (start && end && id) {
        return `Download CSV for report ${id}, date range ${start} to ${end}`
      }
      if (id) return `Download CSV for report ${id}`
      return "Download CSV for this report"
    }

    const handleReload = () => {
      announce(t("common.a11y_loading_pass_totals"))
      getPassReports()
      showLatestReportStatus()
    }

    //GET THE REPORTS FROM BE
    const getPassReports = (page = 1, isLazyLoadingMode) => {
      if (!state) return
      state.isLoading = true
      store
        .dispatch("totalPassReports/getTotalPassReports", {
          page,
          per_page: state?.pagination?.per_page?.value || 25
        })
        .then((response) => {
          const data = response?.data
          if (data && state) {
            if (isLazyLoadingMode) {
              store.commit("totalPassReports/PUSH_PASS_REPORTS", data)
            } else {
              store.commit("totalPassReports/SET_PASS_REPORTS", data)
            }

            if (data?.meta && state?.pagination) {
              state.pagination.total = data.meta.total || 0
              state.pagination.from = data.meta.from || 0
              state.pagination.to = data.meta.to || 0
              state.pagination.pages = Math.ceil(
                (data.meta.total || 0) / (data.meta.per_page || 1)
              )
              state.pagination.activePage = Number(state.pagination.activePage)
              state.pagination = JSON.parse(JSON.stringify(state.pagination))
            }
          }
          if (state) {
            state.isLoading = false
          }
        })
        .catch((err) => {
          if (state) {
            state.isLoading = false
            state.errors = err?.message || "An error occurred"
          }
        })
    }

    const passReports = computed(() => {
      const reports = store.getters["totalPassReports/passReports"]
      return Array.isArray(reports) ? reports : []
    })

    const listOfReportIds = computed(() => {
      return Array.isArray(state?.reportIds) ? state.reportIds : []
    })

    const showLatestReportStatus = () => {
      store
        .dispatch("totalPassReports/getLatestReportStatus")
        .then((response) => {
          const data = response?.data?.data
          if (data?.status === "done") {
            handleReports()
          }
          if (state?.reportStatus) {
            state.reportStatus.latestReportStatus = data?.status || null
            if (data?.filters) {
              try {
                state.reportStatus.latestReportFilters =
                  typeof data.filters === "string"
                    ? JSON.parse(data.filters)
                    : data.filters
              } catch {
                state.reportStatus.latestReportFilters = null
              }
            } else {
              state.reportStatus.latestReportFilters = null
            }
          }
        })
        .catch((err) => {
          if (state) {
            state.errors = err?.message
          }
        })
    }

    const reportIdsWithLatestCount = async () => {
      try {
        const response = await store.dispatch(
          "totalPassReports/getReportIdsWithDifferentCount"
        )
        const data = response?.data?.data
        if (state) {
          state.reportIds = Array.isArray(data) ? data : []
          return state.reportIds
        }
        return []
      } catch (err) {
        if (state) {
          state.errors = err
          state.reportIds = []
        }
        return []
      }
    }

    const reRun = (report) => {
      if (state?.reRunStatus?.reRunButtonText && report?.id) {
        state.reRunStatus.reRunButtonText[report.id] = "In Progress"
      }
      store.dispatch("totalPassReports/updateReportWithLatestCount", report?.id)
    }

    const refresh = async (report) => {
      if (!state || !report?.id) return
      state.isReportReRun[report.id] = true
      try {
        const response = await store.dispatch(
          "totalPassReports/fetchPassReport",
          report.id
        )
        if (state.reRunStatus) {
          if (state.reRunStatus.reportStatus) {
            state.reRunStatus.reportStatus[report.id] =
              response?.data?.data?.status
          }
          if (state.reRunStatus.lastReRunDate) {
            state.reRunStatus.lastReRunDate[report.id] =
              report?.last_rerun_date === response?.data?.data?.last_rerun_date
          }
        }

        const updatedItem = response?.data?.data
        store.commit("totalPassReports/UPDATE_REPORT", updatedItem)

        const resp = await reportIdsWithLatestCount()
        if (resp && state.reRunStatus) {
          if (state.reRunStatus.reRunButtonText) {
            state.reRunStatus.reRunButtonText[report.id] = "Re-run"
          }
          state.isReportReRun[report.id] = false
        }
      } catch (err) {
        if (state) {
          state.errors = err
        }
      }
    }

    onUnmounted(() => {
      detachMainLayoutLazyScroll?.()
    })

    onMounted(() => {
      store.commit("totalPassReports/SET_IS_REPORT_NEW", false)
      if (state?.filterParams?.dates) {
        selectRange(state.filterParams.dates)
      }
      initLazyLoading()
      handleReports()
      showLatestReportStatus()
      reportIdsWithLatestCount()
      if (state?.filterParams) {
        state.filterParams.grade_years = gradeYears.value
          ? gradeYears.value[gradeYears.value.length - 1]
          : state.filterParams.grade_years
      }
    })

    return {
      state,
      gradeYears,
      hasSelectableGradeYears,
      clearFilter,
      optionalDateRanges,
      selectRange,
      setCalendarGradeYears,
      dataTableFields,
      passReports,
      getPassReports,
      submit,
      downloadCsv,
      downloadCsvAriaLabel,
      setActivePage,
      setPerPage,
      handleReload,
      showReloadButtonAndProgressMessage,
      progressMessage,
      reportIdsWithLatestCount,
      reRun,
      refresh,
      showRefresh,
      refreshIconTabindex,
      isReportIdInList,
      getReRunButtonText,
      isReportInProgress,
      getReportStatus,
      getLastReRunDate,
      isReportReRunning,
      isStartDateValid,
      currentSchoolYearSelected,
      activeSchool,
      dismissPassTotalsError
    }
  }
}
</script>

<style scoped>
.blurred-icon {
  opacity: 0.25;
  cursor: not-allowed;
}
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

/* Let focus rings / box-shadow show past scroll/overflow (horizontal table). */
.pass-totals-action-td {
  overflow: visible;
  vertical-align: middle;
}

.pass-totals-action-controls {
  position: relative;
  z-index: 1;
}
</style>
